import Vue from 'vue'
import App from './App.vue'
import router, { ALL_ROUTES } from './router'
import store from './store'
import VueI18n from 'vue-i18n'
import './main.scss'
import './flex'
import mixin from './mixin'
import ActiveTouch from './directives/activeTouch'
import LoadScript from "vue-plugin-load-script";
import { applyHostTheme } from './utils/theme'
import { getMachineConfigErrorRoute, getMapFallbackRoute, getRescanRequiredRoute, shouldForceRescan } from './utils/mapFeature'
import { clearSessionForRescan } from './utils/session'
import { quickLogin } from './api/notLogin'

Vue.config.productionTip = false

// 动态加载JS
Vue.use(LoadScript)
// 多语言
Vue.use(VueI18n)
// 全局指令：touch事件添加移除active
Vue.directive('active-touch', ActiveTouch)

import {cks, getDefaultLanguage} from './utils/tool'
import {langPackage} from "./common/lang";

applyHostTheme();

(async () => {
    // 语言包
    let useLangPackage = langPackage
    // 默认语言
    let defaultLocale = getDefaultLanguage()
    const savedLang = cks.get('lang')
    const lang = savedLang || defaultLocale
    cks.set("lang", lang)
    const i18n = new VueI18n({
        locale: lang,
        messages: useLangPackage
    })

    // 获取基础配置
    async function initConfig() {
        const routePaths = [
            'payment_select',
            'login',
            'register_info',
            'gateway_redirect',
            'moyasar_redirect',
            'recharge_redirect',
            'bind_redirect',
            'payment_redirect',
            'renting_page',
            'repayment_select',
            'borrow_success',
            'landing_page',
            'error',
            'code_verify',
            'email_code_verify',
            'pop_up_directly',
            'machine_config_error',
            'order_info',
            'cabinet_map',
            'help',
            'report_problem',
            'chat_with_us'
        ];

        const hasFetchedAppConfig = sessionStorage.getItem('hasFetchedAppConfig');
        const errorFlag = store.getters['appConfig/error_flag'];
        const rawLastDeviceCode = sessionStorage.getItem('lastDeviceCode') || '';
        const persistedDeviceCode = store.getters['appConfig/device_code'] || '';
        const isKnownRoutePath = value => routePaths.includes(value);
        const lastDeviceCode = isKnownRoutePath(rawLastDeviceCode)
            ? (isKnownRoutePath(persistedDeviceCode) ? '' : persistedDeviceCode)
            : rawLastDeviceCode;

        if (rawLastDeviceCode !== lastDeviceCode) {
            if (lastDeviceCode) {
                sessionStorage.setItem('lastDeviceCode', lastDeviceCode);
            } else {
                sessionStorage.removeItem('lastDeviceCode');
            }
        }

        // 优化机器码识别逻辑：
        // 1. 去除 query string 防止干扰
        // 2. 如果路径包含 gateway_redirect / recharge_redirect 或 bind_redirect，强制视为路由页面（isRoutePath=true），避免将后面的路径段误判为机器码
        const hrefPath = window.location.href.split('?')[0];
        const popCode = hrefPath.split('/').pop();

        let isRoutePath = routePaths.includes(popCode);
        if (hrefPath.includes('/gateway_redirect')) {
            isRoutePath = true;
        }
        if (hrefPath.includes('/moyasar_redirect')) {
            isRoutePath = true;
        }
        if (hrefPath.includes('/recharge_redirect')) {
            isRoutePath = true;
        }
        if (hrefPath.includes('/bind_redirect')) {
            isRoutePath = true;
        }
        if (hrefPath.includes('/payment_redirect')) {
            isRoutePath = true;
        }

        const currentDeviceCode = isRoutePath ? lastDeviceCode : popCode;
        const deviceCodeChanged = currentDeviceCode !== lastDeviceCode;

        if (!isRoutePath && currentDeviceCode) {
            sessionStorage.setItem('lastDeviceCode', currentDeviceCode);
            store.commit('appConfig/SET_DEVICE_CODE', currentDeviceCode);
        }

        // 计算是否需要拉取基础配置：
        // - store 缺少关键配置（例如首次加载或持久化出错）
        // - 尚未标记成功拉取（首次进入或会话重置）
        // - 上次拉取出错（需重试）
        // - 未记录设备码（首次扫码或直达）
        // - 设备码变更（切换二维码/站点）
        const configLoaded = !!store.getters['appConfig/brand_name'] || !!store.getters['appConfig/login_method'];
        const shouldFetch = !configLoaded || !hasFetchedAppConfig || !!errorFlag || !lastDeviceCode || deviceCodeChanged;

        if (shouldFetch) {
            await store.dispatch('appConfig/fetchAppConfig');
            sessionStorage.setItem('hasFetchedAppConfig', true);
        }

        let token = cks.get('token');
        const loginMethod = store.getters['appConfig/login_method'];
        const quickLoginEnabled = Array.isArray(loginMethod) && loginMethod.some(method => method && method.name === 'quick_login');
        const autoQuickLogin = store.getters['appConfig/auto_quick_login'];
        const deviceCode = store.getters['appConfig/device_code'];

        if (!token && quickLoginEnabled && autoQuickLogin && deviceCode) {
            try {
                const res = await quickLogin({ code: deviceCode });
                if (res && res.data && res.data.token) {
                    await store.dispatch('setToken', res.data.token);
                    await store.dispatch('appConfig/fetchAppConfig');
                    sessionStorage.setItem('hasFetchedAppConfig', true);
                    sessionStorage.setItem('lastDeviceCode', store.getters['appConfig/device_code'] || deviceCode);
                }
            } catch (_) {}
            token = cks.get('token');
        }

        const precheckError = store.getters['appConfig/machine_precheck_error'];
        const isMachineErrorLoginFlowPaths = [
            '/machine_config_error',
            '/login',
            '/code_verify',
            '/email_code_verify',
            '/help',
            '/report_problem',
            '/chat_with_us',
        ];
        if (store.getters['appConfig/map_enabled']) {
            isMachineErrorLoginFlowPaths.push('/cabinet_map');
        }
        const isMachineErrorLoginFlow = isMachineErrorLoginFlowPaths
            .some(path => window.location.pathname.includes(path));
        if (precheckError && !token && !isMachineErrorLoginFlow) {
            const route = getMachineConfigErrorRoute(precheckError);
            const query = new URLSearchParams(route.query || {});
            const target = query.toString() ? `${route.path}?${query.toString()}` : route.path;
            window.history.replaceState(null, '', target);
        }

        if (shouldForceRescan({
            mapEnabled: store.getters['appConfig/map_enabled'],
            deviceCode: store.getters['appConfig/device_code'],
            path: window.location.pathname,
        })) {
            if (token) {
                clearSessionForRescan(store);
            }
            const route = getRescanRequiredRoute();
            const query = new URLSearchParams(route.query || {});
            const target = query.toString() ? `${route.path}?${query.toString()}` : route.path;
            window.history.replaceState(null, '', target);
        }

        if (window.location.pathname === '/cabinet_map' && !store.getters['appConfig/map_enabled']) {
            const route = getMapFallbackRoute({
                isBorrow: store.getters.is_borrow,
                deviceCode: store.getters['appConfig/device_code'],
                machinePrecheckError: precheckError,
            });
            const query = new URLSearchParams(route.query || {});
            const target = query.toString() ? `${route.path}?${query.toString()}` : route.path;
            window.history.replaceState(null, '', target);
        }

        if (store.getters['appConfig/app_mode'] === 1) {
            window.location.href = '/pop_up_directly';
        }
        if (store.getters['appConfig/app_mode'] === 2) {
            window.location.href = store.getters['appConfig/redirect_url'];
        }
    }

    await initConfig();

    Vue.mixin(mixin)

    new Vue({
        router,
        store,
        i18n,
        render: h => h(App),

        mounted() {
            const loader = document.getElementById("app-loading");
            if (loader) {
                loader.style.display = "none";
            }
        }
    }).$mount('#app')
})()
